import { useApiClient } from "@/composables/useApiClient";
import { useUtils } from "@/composables/useUtils";
import type { FlowConfig } from "@/config/flowConfig";
import type { Brand } from "@/types/api";
import _ from "lodash";
import { defineStore } from "pinia";
import type { Router } from "vue-router";

interface User {
  email: string | null;
  name: string | null;
  phone: string | null;
}

interface ChecklistItem {
  route: string;
  description: string;
  complete: boolean;
}

interface AppState {
  currentStepIndex: number;
  flowConfig?: FlowConfig;
  brand: string | null;
  brandData: Brand | null;
  insuranceCode: string | null;
  bike: any;
  planId: number | null;
  campaign: string | null;
  plans: any[];
  bikes: any[];
  hasBike: boolean;
  email: string | null;
  clientId: string | null;
  name: string | null;
  phone: string | null;
  cpf: string | null;
  address: any;
  bikeSerialNumber: string | null;
  bikeDANFECode: string | null;
  checklist: Array<ChecklistItem>;
  isClient: boolean;
  isDivergentDANFE: boolean;
  resume: boolean;
  hasRating: boolean;
  photoErrorCount: number;
  user: User;
  tagInfo: any;
  maskedEmail: string | null;
  maskedPhone: string | null;
  tag: string | null;
  renewal: boolean;
  isCampaignAllowed: boolean;
  isCampaignEnabled: boolean;
  storeCampaign: string | null;
  isUserVerified: boolean;
  isCodeRequested: boolean;
  isBrandFreePlanAvailable: boolean;
  isBrandFreePlanEnabled: boolean;
  isFinished: boolean;
  numberOfTutorialSteps?: number;
  appTexts: Record<string, Object>;
  parameters: any;
  selectedVehicleIsScooter: boolean | null;
}

function checkItemComplete(state, item) {
  const check = state.checklist.find((c) => c.route === item);
  if (!check) {
    return false;
  }
  return check.complete;
}

export const getDefaultState = (): AppState => {
  return {
    currentStepIndex: 0,
    flowConfig: undefined,
    brand: null,
    brandData: null,
    insuranceCode: null,
    bike: null,
    planId: null,
    campaign: null,
    plans: [],
    bikes: [],
    hasBike: true,
    email: null,
    clientId: null,
    name: null,
    phone: null,
    cpf: null,
    address: null,
    bikeSerialNumber: null,
    bikeDANFECode: null,
    checklist: [],
    isClient: false,
    isDivergentDANFE: false,
    resume: false,
    hasRating: false,
    photoErrorCount: 0,
    user: {
      email: null,
      name: null,
      phone: null,
    },
    tagInfo: null,
    maskedEmail: null,
    maskedPhone: null,
    tag: null,
    renewal: false,
    isCampaignAllowed: false,
    isCampaignEnabled: false,
    storeCampaign: null,
    isUserVerified: false,
    isCodeRequested: false,
    isBrandFreePlanAvailable: false,
    isBrandFreePlanEnabled: false,
    isFinished: false,
    numberOfTutorialSteps: null,
    appTexts: {},
    parameters: null,
    selectedVehicleIsScooter: null,
  };
};

export const useAppStore = defineStore("appStore", {
  state: () => getDefaultState(),
  getters: {
    capitalizedBrand: (state) => state.brand.charAt(0).toUpperCase() + state.brand.slice(1),
    bikeByType: (state) => (typeId) =>
      state.bikes.filter((b) => typeId === -1 || b.bikeType.id === typeId),
    isItemComplete: (state) => (item) => checkItemComplete(state, item),
    isAllComplete: (state) => !_.some(state.checklist, ["complete", false]),
    selectedPlan: (state) => state.plans.find((p) => p.id === state.planId),
    isBasicFreeCampaign: (state) => state.isCampaignEnabled && state.campaign === "BASIC_FREE",
    isBasicPlan: (state) => state.planId === 1,
    currentStep: (state) => {
      return state.flowConfig?.orderedPageRoutes[state.currentStepIndex] ?? null;
    },
    hasNextStep: (state) => {
      return (
        state.flowConfig && state.currentStepIndex < state.flowConfig.orderedPageRoutes.length - 1
      );
    },
    hasPreviousStep: (state) => {
      return state.currentStepIndex > 0;
    },
  },
  actions: {
    setSelectedVehicleIsScooter(scooter: boolean) {
      this.selectedVehicleIsScooter = scooter;
    },
    nextStep(router: Router) {
      if (this.flowConfig && this.currentStepIndex < this.flowConfig.orderedPageRoutes.length - 1) {
        this.currentStepIndex++;
        const nextPage = this.flowConfig.orderedPageRoutes[this.currentStepIndex];
        router.push({ name: nextPage.replace(".vue", "") });
      }
    },
    previousStep(router: Router) {
      if (this.currentStepIndex > 0) {
        this.currentStepIndex--;
        const prevPage = this.flowConfig.orderedPageRoutes[this.currentStepIndex];
        router.push({ name: prevPage.replace(".vue", "") });
      }
    },
    goToStep(index: number, router: Router) {
      if (this.flowConfig && index >= 0 && index < this.flowConfig.orderedPageRoutes.length) {
        this.currentStepIndex = index;
        const page = this.flowConfig.orderedPageRoutes[index];
        router.push({ name: page.replace(".vue", "") });
      }
    },
    setFlowConfig(config: FlowConfig) {
      this.flowConfig = config;
      this.currentStepIndex = 0;
    },
    resetState() {
      const preserved = {
        brand: this.brand,
      };
      Object.assign(this, getDefaultState(), preserved);
    },
    setBrand(brand: string) {
      this.brand = brand;
    },
    loadBrandData(insuranceCode?: string) {
      const apiClient = useApiClient();
      return apiClient.loadBrandData(insuranceCode).then((res: any) => {
        this.brandData = res;
        this.setBrand(res.name);
        return res;
      });
    },
    setPhotoError() {
      this.photoErrorCount += 1;
    },
    enableCampaign() {
      const apiClient = useApiClient();

      return apiClient.enableCampaign(this.insuranceCode).then((res) => {
        const data = res.data.data;
        //TODO REVISTAR O PORQUE DISSO NÃO EXISTIR
        // this.loadTagData(data);
        return res;
      });
    },
    disableCampaign() {
      const apiClient = useApiClient();

      return apiClient.disableCampaign(this.insuranceCode).then((res) => {
        const data = res.data.data;
        //TODO REVISTAR O PORQUE DISSO NÃO EXISTIR
        // this.loadTagData(data);
        return res;
      });
    },
    tokenCard(data) {
      const apiClient = useApiClient();

      return apiClient.tokenCard(data).then((res) => {
        return res.data.id;
      });
    },
    listBikes() {
      const apiClient = useApiClient();

      return apiClient.listBikes(this.insuranceCode).then((res) => {
        const bikeList = res.data;
        bikeList.sort((a, b) => a.model.localeCompare(b.model));
        this.bikes = bikeList;
        return res;
      });
    },
    setPlan(planId) {
      const apiClient = useApiClient();

      //TODO REVISAR O PORQUE DISSO NÃO EXISTIR
      // return apiClient.setPlan(this.insuranceCode, planId).then((res) => {
      //   this.planId = planId;
      //   return res;
      // });
    },
    setUser(user) {
      const apiClient = useApiClient();

      console.log("setUser", user);

      return apiClient.setUser(user, this.insuranceCode).then((res) => {
        this.user = user;
        const clientId = typeof res.data === "number" ? res.data.toString() : res.data;
        this.clientId = clientId;
        this.isCodeRequested = true;

        return res;
      });
    },
    requestConfirmationCode() {
      if (!this.clientId) return;

      return this.setUser(this.user);
    },
    requestConfirmationForInsurance(insuranceCode) {
      const apiClient = useApiClient();

      return apiClient
        .sendConfirmationCode(insuranceCode)
        .then(() => (this.isCodeRequested = true));
    },
    verifyEmail(code) {
      const apiClient = useApiClient();

      return apiClient.verifyEmail(this.clientId, code, this.insuranceCode).then((res) => {
        if (res && res.data && typeof res.data === "object") {
          const clientData = res.data as any;
          this.name = clientData.name;
          this.phone = clientData.phone;
          this.cpf = clientData.cpf;
        }
        this.isCodeRequested = false;
        return res;
      });
    },
    setDocument({ type, documentFront, documentBack, latitude, longitude }) {
      const apiClient = useApiClient();

      return apiClient.setDocument(
        this.insuranceCode,
        type,
        documentFront,
        documentBack,
        latitude,
        longitude,
      );
    },
    setAddress(address) {
      const apiClient = useApiClient();

      return apiClient.setAddress(this.insuranceCode, address).then((res) => {
        this.address = address;
        return res;
      });
    },
    loadPlans() {
      const apiClient = useApiClient();

      return apiClient.listBikePlans(this.insuranceCode).then((res) => {
        this.plans = res.data;
        return res;
      });
    },
    getChecklist() {
      const apiClient = useApiClient();

      return apiClient.getChecklist(this.insuranceCode).then((res) => {
        this.checklist = res.data;
        return res;
      });
    },
    setCardInfo(cardInfo) {
      const apiClient = useApiClient();

      return apiClient.setCardInfo(this.insuranceCode, cardInfo).then((res) => {
        return res;
      });
    },
    sendRating(state) {
      const apiClient = useApiClient();

      return apiClient.sendRating(state).then((res) => {
        this.hasRating = true;
        return res;
      });
    },
    getCep(cep) {
      const apiClient = useApiClient();

      return apiClient.getCep(cep).then((res) => {
        return res.data;
      });
    },
    checkIfBikeHasTag() {
      const apiClient = useApiClient();

      return apiClient
        .checkIfBikeHasTag(this.bikeSerialNumber, this.brand, this.insuranceCode)
        .then((res) => {
          return res.data;
        });
    },
    checkBrandFreePlan() {
      const apiClient = useApiClient();

      return apiClient.checkEligibleBrandFreePlan(this.insuranceCode).then((res) => {
        this.isBrandFreePlanAvailable = res.data;
        return res.data;
      });
    },
    enableBrandFreePlan() {
      const apiClient = useApiClient();

      return apiClient.enableBrandFreePlan(this.insuranceCode).then((res) => {
        this.isBrandFreePlanEnabled = res.data.data;
        return res.data.data;
      });
    },
    setNumberOfTutorialSteps(steps: number) {
      this.numberOfTutorialSteps = steps;
    },
    setTexts(texts: Record<string, Object>) {
      this.appTexts = texts;
    },
    setParameters(params: any) {
      this.parameters = params;
    },
    setInsuranceCode(insuranceCode: string) {
      this.insuranceCode = insuranceCode;
    },
    retrieveInsuranceProgress() {
      const apiClient = useApiClient();

      return apiClient
        .retrieveInsuranceProgress(this.insuranceCode, useUtils().channel)
        .then((res) => {
          console.log("retrieveInsuranceProgress", res.data);
          return res.data;
        });
    },
    // Actions nao utilizadas no fluxo
    initInsuranceWithStore(storeToken, tag) {
      const apiClient = useApiClient();
      return apiClient.initInsurance(storeToken, tag).then((res) => {
        this.insuranceCode = res.data;
        return res;
      });
    },
  },
  persist: {
    storage: sessionStorage,
  },
});
